import theme from "assets/themes";
import Icon from "components/atoms/Icon/Icon";
import { sidebar } from "components/atoms/SidebarContainer";
import { useEffect, useMemo, useState } from "react";
import { Link, matchPath, useMatch, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { handleToggleSidebar } from "store/slices/ui.slice";
import { EUserRoles } from "types/user/user.type";
import { sidebarItems } from "utils/config";

export const Sidebar = () => {
  const { sidebarOpen } = useAppSelector((state) => state.ui);
  const dispatch = useAppDispatch();
  const { data: user } = useAppSelector((state) => state.user);
  const isAdmin = useMemo(() => {
    return user?.userRole === EUserRoles.ADMIN;
  }, [user?.userRole]);
  const toggleSidebar = () => {
    dispatch(handleToggleSidebar());
  };
  const { base, logo, logoImg, header, list } = sidebar({
    open: sidebarOpen,
    active: false,
  });

  return (
    <aside className={base()}>
      <div className={header()}>
        <Link to="/" className="hidden md:flex">
          <div className={logo()}>
            <img src={theme["logo"]} className={logoImg()} alt="logo" />
          </div>
        </Link>
        <div
          className="flex flex-1 text-white-500 h-full md:hidden justify-center items-center"
          onClick={toggleSidebar}
        >
          <Icon name="menu" size={22} className="" />
        </div>
      </div>
      <button
        onClick={toggleSidebar}
        className="hidden md:block hover:bg-gray-800 text-white-500 bg-gray-700 p-2 rounded-md border border-gray-800 absolute top-[54px] -right-4"
      >
        <Icon name={sidebarOpen ? "chevron-left" : "chevron-right"} size={12} />
      </button>
      <ul className={`${list()}  overflow-y-auto max-h-[calc(100vh-54px)]`}>
        {sidebarItems.map((item) => (
          <SidebarItem
            key={item.title}
            icon={item.icon}
            title={item.title}
            openSidebar={sidebarOpen}
            subItems={item.subRoutes}
            baseUrl={item.baseUrl}
            isAdmin={isAdmin}
            toggleSidebar={toggleSidebar}
          />
        ))}
      </ul>
    </aside>
  );
};
const SidebarItem = ({
  icon,
  title,
  openSidebar,
  subItems,
  baseUrl,
  isAdmin,
  toggleSidebar,
}: {
  icon: string;
  title: string;
  isAdmin: boolean;
  openSidebar: boolean;
  baseUrl: string;
  toggleSidebar: () => void;
  subItems?: { label: string; url: string; onlyAdmin?: boolean }[];
}) => {
  const [openSubItems, setOpenSubItems] = useState(false);
  const isMatch = useMatch({
    path: `${baseUrl}/*`,
    end: false,
  });
  const navigate = useNavigate();
  const { item, submenu, link } = sidebar({
    open: openSidebar,
    active: !!isMatch,
    showSubItems: openSubItems,
  });

  const handleOpenSubItems = (event: React.MouseEvent) => {
    event.preventDefault();
    if (openSidebar === false) {
      toggleSidebar();
      setOpenSubItems(true);
      return;
    }
    if (!!subItems?.length) {
      setOpenSubItems((oldState) => !oldState);
      return;
    }
    navigate(`${baseUrl}`);
  };

  useEffect(() => {
    if (openSidebar === false) {
      setOpenSubItems(false);
    }
  }, [openSidebar]);

  return (
    <li className="w-full flex flex-col justify-center relative" key={title}>
      <div onClick={handleOpenSubItems} className={item()}>
        <div className="flex w-full gap-4 items-center">
          <div className="max-w-[20px] max-h-[20px]">
            <Icon name={icon} size={18} />
          </div>
          <span className={` text-xs text-nowrap font-semibold`}>{title}</span>
        </div>
        <div className="max-w-[16px] max-h-[16px] w-full mr-auto">
          {!!subItems?.length ? (
            <Icon
              name={openSubItems ? "chevron-up" : "chevron-down"}
              size={16}
            />
          ) : null}
        </div>
      </div>
      {subItems && subItems.length > 0 ? (
        <div className={submenu()}>
          {openSubItems
            ? subItems?.map((item, index) => {
                const url = `${baseUrl}${item.url ? `${item.url}` : ""}`;
                const isMatch = matchPath(
                  { path: url, end: false },
                  location.pathname
                );
                if (item.onlyAdmin && !isAdmin) return null;
                return (
                  <Link
                    to={url}
                    onClick={() => {
                      !openSidebar && setOpenSubItems(false);
                    }}
                    key={`${baseUrl}/${item.url}`}
                    className={link({ active: !!isMatch })}
                  >
                    {item.label}
                  </Link>
                );
              })
            : null}
        </div>
      ) : null}
    </li>
  );
};
